import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore, ServiceStore } from "@intrasync/stores";
import type { evereeWorkerData } from "types/api-types/payroll/everee-worker-data/everee-worker-data.types";
import type { user as User } from "types/api-types/users/users.types";

export type LeaveType = "Vacation" | "PTO";
export type PolicyType = "salary" | "hourly";

export interface TitanLeaveRow {
  employeeName: string;
  employeeNumber: string;
  vacationBalance: number;
  ptoBalance: number;
  sickBalance: number;
}

export interface MappedLeaveEntry {
  titanName: string;
  titanNumber: string;
  evereeWorkerId: string;
  evereeWorkerName: string;
  leaveType: LeaveType;
  hours: number;
  matched: boolean;
}

class LeaveBalancesStore {
  rootStore: RootStore;

  // Data
  workers: evereeWorkerData[] = [];
  companyUsers: User[] = [];
  titanRows: TitanLeaveRow[] = [];
  mappedEntries: MappedLeaveEntry[] = [];

  // UI state
  step = 0; // 0=Upload, 1=Review/Map, 2=Export
  isLoading = false;
  error: string | null = null;
  successMessage: string | null = null;
  policyType: PolicyType = "hourly";
  search = "";

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  get companyId(): string {
    return (this.rootStore.routerStore?.queryParams?.companyId as string) || "NTX";
  }

  get evereeWorkerDataStore(): ServiceStore {
    return this.rootStore.evereeWorkerDataStore as ServiceStore;
  }

  get userStore(): ServiceStore {
    return this.rootStore.userStore as ServiceStore;
  }

  get filteredEntries(): MappedLeaveEntry[] {
    if (!this.search) return this.mappedEntries;
    const q = this.search.toLowerCase();
    return this.mappedEntries.filter(
      (e) =>
        e.titanName.toLowerCase().includes(q) ||
        e.evereeWorkerName.toLowerCase().includes(q) ||
        e.titanNumber.includes(q)
    );
  }

  get unmatchedCount(): number {
    return this.mappedEntries.filter((e) => !e.matched).length;
  }

  get matchedCount(): number {
    return this.mappedEntries.filter((e) => e.matched).length;
  }

  setStep = (step: number) => {
    this.step = step;
  };

  setPolicyType = (type: PolicyType) => {
    this.policyType = type;
  };

  setSearch = (search: string) => {
    this.search = search;
  };

  loadData = async (companyId?: string) => {
    this.isLoading = true;
    this.error = null;
    const cid = companyId || this.companyId;

    try {
      await Promise.all([
        this.evereeWorkerDataStore.find({
          query: { companyId: cid, page: 0, size: 200 },
        }),
        this.userStore.find({
          query: {
            companyId: cid,
            tcpWorkerId: { $ne: null },
            evereeWorkerId: { $ne: null },
            $limit: 500,
          },
        }),
      ]);

      runInAction(() => {
        const rawWorkers = (this.evereeWorkerDataStore.entities || []) as unknown as Record<string, unknown>[];
        this.workers = rawWorkers.map((w) => ({
          ...w,
          workerId: (w.workerId || w.id) as string,
        })) as unknown as evereeWorkerData[];

        this.companyUsers = (this.userStore.entities || []).filter(
          (u: unknown) => {
            const user = u as Record<string, unknown>;
            return user.tcpWorkerId && user.evereeWorkerId;
          }
        ) as unknown as User[];
      });
    } catch (err) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load data";
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  parseTitanCsv = (text: string) => {
    this.error = null;

    try {
      const lines = text.split("\n").filter((l) => l.trim());
      if (lines.length < 2) {
        this.error = "CSV file appears empty or has no data rows";
        return;
      }

      // Parse headers — flexible matching
      const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
      const nameIdx = headers.findIndex((h) =>
        h.includes("name") || h.includes("employee")
      );
      const numIdx = headers.findIndex((h) =>
        h.includes("number") || h.includes("id") || h.includes("emp#")
      );
      const vacIdx = headers.findIndex((h) =>
        h.includes("vacation") || h.includes("vac")
      );
      const ptoIdx = headers.findIndex((h) => h.includes("pto"));
      const sickIdx = headers.findIndex((h) => h.includes("sick"));

      if (nameIdx === -1) {
        this.error = "Could not find employee name column in CSV";
        return;
      }

      const rows: TitanLeaveRow[] = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",").map((c) => c.trim().replace(/^"|"$/g, ""));
        const name = cols[nameIdx] || "";
        if (!name) continue;

        rows.push({
          employeeName: name,
          employeeNumber: numIdx >= 0 ? (cols[numIdx] || "") : "",
          vacationBalance: vacIdx >= 0 ? parseFloat(cols[vacIdx]) || 0 : 0,
          ptoBalance: ptoIdx >= 0 ? parseFloat(cols[ptoIdx]) || 0 : 0,
          sickBalance: sickIdx >= 0 ? parseFloat(cols[sickIdx]) || 0 : 0,
        });
      }

      runInAction(() => {
        this.titanRows = rows;
        this.autoMapEntries();
        this.step = 1;
      });
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to parse CSV";
    }
  };

  handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      if (text) this.parseTitanCsv(text);
    };
    reader.onerror = () => {
      this.error = "Failed to read file";
    };
    reader.readAsText(file);
  };

  autoMapEntries = () => {
    const entries: MappedLeaveEntry[] = [];

    for (const row of this.titanRows) {
      // Try saved user mappings first (by TCP employee number)
      const savedUser = this.companyUsers.find(
        (u: unknown) => {
          const user = u as Record<string, unknown>;
          return user.tcpWorkerId === row.employeeNumber;
        }
      );

      let evereeWorkerId = "";
      let evereeWorkerName = "";
      let matched = false;

      if (savedUser) {
        const su = savedUser as unknown as Record<string, unknown>;
        evereeWorkerId = su.evereeWorkerId as string;
        const worker = this.workers.find((w) => w.workerId === evereeWorkerId);
        evereeWorkerName = worker
          ? `${worker.firstName} ${worker.lastName}`
          : (evereeWorkerId as string);
        matched = true;
      } else {
        // Try name match against Everee workers
        const nameLower = row.employeeName.toLowerCase().trim();
        const worker = this.workers.find((w) => {
          const full = `${w.firstName} ${w.lastName}`.toLowerCase();
          const reversed = `${w.lastName} ${w.firstName}`.toLowerCase();
          const lastFirst = `${w.lastName}, ${w.firstName}`.toLowerCase();
          return full === nameLower || reversed === nameLower || lastFirst === nameLower;
        });

        if (worker) {
          evereeWorkerId = worker.workerId;
          evereeWorkerName = `${worker.firstName} ${worker.lastName}`;
          matched = true;
        }
      }

      // Add Vacation entry (skip if 0)
      if (row.vacationBalance > 0) {
        entries.push({
          titanName: row.employeeName,
          titanNumber: row.employeeNumber,
          evereeWorkerId,
          evereeWorkerName,
          leaveType: "Vacation",
          hours: row.vacationBalance,
          matched,
        });
      }

      // Add PTO entry (skip if 0)
      if (row.ptoBalance > 0) {
        entries.push({
          titanName: row.employeeName,
          titanNumber: row.employeeNumber,
          evereeWorkerId,
          evereeWorkerName,
          leaveType: "PTO",
          hours: row.ptoBalance,
          matched,
        });
      }

      // Skip Sick per plan requirements
    }

    this.mappedEntries = entries;
  };

  updateEntryMapping = (index: number, workerId: string) => {
    if (index < 0 || index >= this.mappedEntries.length) return;

    const worker = this.workers.find((w) => w.workerId === workerId);
    this.mappedEntries[index] = {
      ...this.mappedEntries[index],
      evereeWorkerId: workerId,
      evereeWorkerName: worker
        ? `${worker.firstName} ${worker.lastName}`
        : workerId,
      matched: !!workerId,
    };
  };

  generateEvereeTemplate = (): string => {
    const matchedEntries = this.mappedEntries.filter((e) => e.matched);
    if (matchedEntries.length === 0) return "";

    // Everee leave balance template format:
    // Worker ID, Leave Type, Policy Type, Balance (hours)
    const rows = [
      "worker_id,leave_type,policy_type,balance_hours",
      ...matchedEntries.map(
        (e) =>
          `${e.evereeWorkerId},${e.leaveType},${this.policyType},${e.hours.toFixed(2)}`
      ),
    ];

    return rows.join("\n");
  };

  downloadTemplate = () => {
    const csv = this.generateEvereeTemplate();
    if (!csv) {
      this.error = "No matched entries to export";
      return;
    }

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `everee-leave-balances-${this.companyId}-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);

    this.successMessage = `Exported ${this.mappedEntries.filter((e) => e.matched).length} leave balance entries`;
    this.step = 2;
  };

  reset = () => {
    this.step = 0;
    this.isLoading = false;
    this.error = null;
    this.successMessage = null;
    this.titanRows = [];
    this.mappedEntries = [];
    this.policyType = "hourly";
    this.search = "";
  };
}

export default LeaveBalancesStore;
